import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Chip,
  IconButton,
  Typography,
  //   Divider,
  Paper,
  Button,
  useTheme,
  Skeleton,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { DataGridPro } from "@mui/x-data-grid-pro";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StraightenIcon from "@mui/icons-material/Straighten";
import BugReportIcon from "@mui/icons-material/BugReport";
import defaultConfigStore from "../../stores/DefaultConfigStore";

const DefaultConfigDetail = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { deviceTypeRecord } = location.state || {};

  console.log("Here is deviceType record: ", deviceTypeRecord);

  const deviceTypeId = deviceTypeRecord?.id;
  const deviceTypeName = deviceTypeRecord?.type_name || "Unknown Device Type";

  const config = defaultConfigStore.getDefaultConfig(deviceTypeId);

  console.log(
    "Rendered with config:",
    config?.alertsConfig?.systemVoltage?.threshold
  );

  const loading = defaultConfigStore.loading;
  const error = defaultConfigStore.error;

  const [activeTab, setActiveTab] = useState("Summary");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const tabList = ["Summary", "Raw Config", "Some Tab"];

  console.log("Here is default alert config detail page!");

  const buildPageNumbers = useCallback((total, current) => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    const range = [1];
    if (current > 3) range.push("...");
    for (
      let i = Math.max(2, current - 1);
      i <= Math.min(total - 1, current + 1);
      i++
    ) {
      range.push(i);
    }
    if (current < total - 2) range.push("...");
    range.push(total);
    return range;
  }, []);

  const flattenFaultCode = useCallback((obj, prefix = "") => {
    const result = [];
    for (const [k, v] of Object.entries(obj)) {
      if (v?.type === "fault-code") {
        result.push({ key: `${prefix}${k}`, ...v });
      } else if (typeof v === "object") {
        result.push(...flattenFaultCode(v, `${prefix}${k}.`));
      }
    }
    return result;
  }, []);

  const pageNumbers = useMemo(() => {
    if (!config) return [];
    const alertsConfig = config.alertsConfig ?? {};
    const oneBound = Object.values(alertsConfig).filter(
      (v) => v?.type === "one-bound"
    );
    const twoBound = Object.values(alertsConfig).filter(
      (v) => v?.type === "two-bound"
    );
    const faultCodes = flattenFaultCode(alertsConfig);
    const total = Math.max(
      Math.ceil(
        (oneBound.length + twoBound.length + faultCodes.length) / pageSize
      ),
      1
    );
    const current = Math.min(page, total);
    return buildPageNumbers(total, current);
  }, [config, pageSize, page, buildPageNumbers, flattenFaultCode]);

  useEffect(() => {
    if (!config && !loading) {
      defaultConfigStore.fetchAllDefaultConfigs();
    }
  }, [config, loading]);

  if (!deviceTypeRecord) {
    return (
      <Box p={3}>
        <Typography color="error">Missing device type data.</Typography>
      </Box>
    );
  }

  if (loading || !config) {
    return (
      <Box p={3}>
        <Box className="flex mb-2 gap-x-2" sx={{ alignItems: "center" }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">
            Default Configuration for: {deviceTypeName}
          </Typography>
        </Box>
        <Skeleton
          variant="rectangular"
          height={60}
          sx={{ my: 2, borderRadius: 2 }}
        />
        <Skeleton
          variant="rectangular"
          height={160}
          sx={{ mb: 2, borderRadius: 2 }}
        />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
        <Button onClick={() => defaultConfigStore.fetchAllDefaultConfigs()}>
          Retry
        </Button>
      </Box>
    );
  }

  const alertsConfig = config.alertsConfig ?? {};

  const oneBoundAlerts = Object.entries(alertsConfig)
    .filter(([, value]) => value?.type === "one-bound")
    .map(([key, value]) => ({ key, ...value }));

  const twoBoundAlerts = Object.entries(alertsConfig)
    .filter(([, value]) => value?.type === "two-bound")
    .map(([key, value]) => ({ key, ...value }));

  const faultCodeAlerts = flattenFaultCode(alertsConfig);

  const allAlerts = [
    ...oneBoundAlerts.map((a) => ({ ...a, _category: "one-bound" })),
    ...twoBoundAlerts.map((a) => ({ ...a, _category: "two-bound" })),
    ...faultCodeAlerts.map((a) => ({ ...a, _category: "fault-code" })),
  ];

  const totalPages = Math.max(Math.ceil(allAlerts.length / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = allAlerts
    .map((row, i) => ({ id: i, ...row }))
    .slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const formatMetricKey = (key) =>
    key
      .split(".")
      .map((seg) =>
        seg.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
      )
      .join(" › ");

  const columns = [
    {
      field: "key",
      headerName: "Metric",
      flex: 1,
      renderCell: ({ value }) => formatMetricKey(value),
    },
    {
      field: "_category",
      headerName: "Type",
      width: 160,
      renderCell: ({ value }) => (
        <Chip
          label={value}
          color={
            {
              "one-bound": "warning",
              "two-bound": "info",
              "fault-code": "error",
            }[value]
          }
          variant="outlined"
        />
      ),
    },
    { field: "triggerMessage", headerName: "Trigger Message", flex: 2 },
    { field: "threshold", headerName: "Threshold", width: 120 },
    { field: "boundary", headerName: "Boundary", width: 100 },
    { field: "low", headerName: "Low", width: 100 },
    { field: "high", headerName: "High", width: 100 },
    { field: "faultCode", headerName: "Fault Code", width: 180 },
  ];

  return (
    <>
      <Box className="flex mb-2 gap-x-2" sx={{ alignItems: "center" }}>
        <IconButton onClick={() => navigate("/alerts")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          Default Configuration for: {deviceTypeName}
        </Typography>
      </Box>

      <Box className="flex gap-2 mb-3">
        {tabList.map((tab) => (
          <Box
            key={tab}
            sx={{
              borderBottom: "2px solid",
              borderColor:
                activeTab === tab
                  ? theme.palette.mode === "dark"
                    ? theme.palette.primary.main
                    : "#22C55E"
                  : "transparent",
            }}
          >
            <Button
              onClick={() => setActiveTab(tab)}
              sx={{
                textTransform: "none",
                color:
                  activeTab === tab
                    ? theme.palette.mode === "dark"
                      ? theme.palette.primary.main
                      : "#000000"
                    : theme.palette.text.primary,
              }}
            >
              {tab}
            </Button>
          </Box>
        ))}
      </Box>

      {activeTab === "Summary" && (
        <>
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            {/* Summary Cards */}
            {/* One-Bound */}
            <Box
              sx={{
                backgroundColor: "#fff3e0",
                borderLeft: "6px solid #ff9800",
                px: 2,
                py: 1.5,
                borderRadius: 1,
                flex: "1 1 260px",
                display: "flex",
                alignItems: "center",
                boxShadow: 2,
              }}
            >
              <WarningAmberIcon sx={{ mr: 1, color: "#ff9800" }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="warning.dark"
                >
                  One-Bound Alerts
                </Typography>
                <Typography variant="body2">
                  {oneBoundAlerts.length} configured threshold alert
                  {oneBoundAlerts.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>

            {/* Two-Bound */}
            <Box
              sx={{
                backgroundColor: "#e3f2fd",
                borderLeft: "6px solid #2196f3",
                px: 2,
                py: 1.5,
                borderRadius: 1,
                flex: "1 1 260px",
                display: "flex",
                alignItems: "center",
                boxShadow: 2,
              }}
            >
              <StraightenIcon sx={{ mr: 1, color: "#2196f3" }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="info.dark"
                >
                  Two-Bound Alerts
                </Typography>
                <Typography variant="body2">
                  {twoBoundAlerts.length} configured range alert
                  {twoBoundAlerts.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>

            {/* Fault Code */}
            <Box
              sx={{
                backgroundColor: "#ffebee",
                borderLeft: "6px solid #f44336",
                px: 2,
                py: 1.5,
                borderRadius: 1,
                flex: "1 1 260px",
                display: "flex",
                alignItems: "center",
                boxShadow: 2,
              }}
            >
              <BugReportIcon sx={{ mr: 1, color: "#f44336" }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="error.dark"
                >
                  Fault-Code Alerts
                </Typography>
                <Typography variant="body2">
                  {faultCodeAlerts.length} fault-code alert
                  {faultCodeAlerts.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              className="!normal-case"
              onClick={() =>
                navigate(`/alerts/default-config/${deviceTypeId}/edit`, {
                  state: {
                    deviceTypeRecord: deviceTypeRecord,
                    mode: "default",
                  },
                })
              }
            >
              Edit Existing Alert Config
            </Button>
          </Box>

          <Box
            sx={{
              height: 600,
              width: "100%",
              boxShadow: 3,
              borderRadius: 1,
              mt: 3,
            }}
          >
            <DataGridPro
              rows={paginatedRows}
              columns={columns}
              density="comfortable"
              disableColumnMenu
              disableRowSelectionOnClick
              isCellEditable={() => false}
              hideFooter
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell": {
                  cursor: "default",
                },
              }}
            />
          </Box>

          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
            }}
          >
            <Button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Prev
            </Button>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {pageNumbers.map((p, i) => (
                <Button
                  key={i}
                  onClick={() => setPage(p)}
                  variant={p === currentPage ? "contained" : "text"}
                  disabled={p === "..."}
                >
                  {p}
                </Button>
              ))}
            </Box>
            <Button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography>Rows per page:</Typography>
              <Select
                size="small"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </>
      )}

      {activeTab === "Raw Config" && (
        <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {JSON.stringify(alertsConfig, null, 2)}
          </Typography>
        </Paper>
      )}

      {activeTab === "Some Tab" && (
        <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
          <Typography variant="body1">Some Tab Content</Typography>
        </Paper>
      )}
    </>
  );
});

export default DefaultConfigDetail;
